// Stewardship Productions documents: who may see and send them.
//
// CONSUMER: shared (Workers routes + web + node-pool tests).
//
// Grant, 2026-09-28: "We need to have an e sign package or templates
// for Stewardship Productions for internal employee and contractor and
// onboarding stuff that needs to get esigned."
//
// Productions is the support entity: it employs the team, it serves no
// household. Its documents are personnel paperwork (offer letters,
// contractor agreements, handbook acknowledgments, and possibly a W-4
// or I-9, which carry a Social Security number). So its library and
// its signed copies follow a NARROWER rule than the five client-facing
// companies, and it is written down once, here, so the template
// routes, the send path and the document reads cannot drift apart.
//
// WHO. In order, first match wins:
//   1. system_admin (Grant, Baker) → yes.
//   2. compliance with NO company scope (firm-wide compliance) → yes.
//      Compliance with a company scope (Cambria, Mortgage only) → no;
//      the same line the health tier draws.
//   3. Anyone whose company_scope names "productions" explicitly → yes.
//      This is how Grant opens it to someone else (Andrea, say) later:
//      one scope edit, no code change.
//   4. Everyone else → no. Including an UNSCOPED account (front desk):
//      a null scope means "reads across the five client companies",
//      never "sees the team's personnel files".
//
// WHERE PERSONNEL FILES LIVE. Two kinds of record, both reusing
// document entity types the documents table has always allowed, so no
// table rebuild was needed:
//   * "user" + a users.id   — a team member already in the OS. Their
//     own personnel file. They may read their own signed copies.
//   * "other" + the Productions company id — the general Productions
//     personnel file, for someone not in the OS (a new hire before day
//     one, an outside contractor).
//
// Pure and dependency-free (the field-types.ts rule), so the decision
// about who reads someone's W-4 is testable directly.

/** The Productions company slug (companies.slug). */
export const PRODUCTIONS_SLUG = "productions";

/** companies.id for Stewardship Productions (migration 0017). */
export const PRODUCTIONS_COMPANY_ID = "00000000-0000-0000-0000-000000000006";

/** The five client-facing companies, in the order every switcher shows
 *  them. The e-sign libraries expanded "a global role" to exactly this
 *  list before Productions existed there. */
export const ESIGN_CLIENT_COMPANIES: readonly string[] = [
  "mortgage",
  "insurance",
  "planning",
  "realty",
  "financial",
];

/** The narrow shape this module reads, matching ActorCompanyScope. */
export interface ProductionsActor {
  roleSlug: string | null;
  /** null = unscoped; [] = malformed (denies). */
  companyScope: readonly string[] | null;
}

/** May this person see, map and send Productions documents? */
export function mayHandleProductionsDocuments(actor: ProductionsActor): boolean {
  if (actor.roleSlug === "system_admin") return true;
  if (actor.roleSlug === "compliance" && actor.companyScope === null) return true;
  return !!actor.companyScope && actor.companyScope.includes(PRODUCTIONS_SLUG);
}

/**
 * The companies whose e-sign library (templates, industry documents,
 * packages) this person can reach, as a concrete list.
 *
 * For the five client companies this is the rule the libraries already
 * had: a global role (system_admin / compliance) reaches all five,
 * anyone else reaches their own company_scope. Productions is then
 * added or removed by mayHandleProductionsDocuments, whatever the
 * scope column says, so a global role never reaches it by accident
 * and a scope naming it never reaches it without the rule agreeing.
 */
export function esignLibraryCompanies(
  actor: ProductionsActor,
  isGlobalRole: boolean,
): string[] {
  const base = isGlobalRole ? ESIGN_CLIENT_COMPANIES : (actor.companyScope ?? []);
  const clientOnly = base.filter((slug) => slug !== PRODUCTIONS_SLUG);
  return mayHandleProductionsDocuments(actor) ? [...clientOnly, PRODUCTIONS_SLUG] : clientOnly;
}

/**
 * Is this document part of a personnel file?
 *
 * Only the two shapes above. A document filed on a household, a
 * contact or a workflow record is never a personnel file, whatever it
 * is called.
 */
export function isPersonnelDocument(entityType: string | null, entityId: string | null): boolean {
  if (entityType === "user") return true;
  return entityType === "other" && entityId === PRODUCTIONS_COMPANY_ID;
}

/**
 * May this person read this personnel document?
 *
 * The Productions rule, plus one more door: a team member may read the
 * documents in their OWN personnel file (their own signed offer letter).
 * Never anyone else's.
 */
export function mayReadPersonnelDocument(
  actor: ProductionsActor & { userId: string },
  entityType: string | null,
  entityId: string | null,
): boolean {
  if (!isPersonnelDocument(entityType, entityId)) return true;
  if (mayHandleProductionsDocuments(actor)) return true;
  return entityType === "user" && entityId === actor.userId;
}
